import axios from "axios";
import { logout } from "../slices/authSlice";
import { toast } from "react-toastify";

const AUTH_ERROR_DEDUPE_WINDOW_MS = 3000;
let lastAuthErrorHandledAt = 0;

export const handleAuthError = (error, dispatch, navigate) => {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return false;

  const now = Date.now();
  if (now - lastAuthErrorHandledAt < AUTH_ERROR_DEDUPE_WINDOW_MS) {
    return true;
  }
  lastAuthErrorHandledAt = now;

  if (dispatch) dispatch(logout());
  if (navigate) navigate("/login");
  toast.error(
    error?.response?.data?.message || "Session expired. Please login again."
  );
  return true;
};

export const validateToken = (token) => {
  if (token && token !== "undefined") {
    try {
      return token;
    } catch (error) {
      console.error("Error validating token:", error);
    }
  }
  return null;
};

const latestPageRequest = {};

let pendingRequests = 0;
let idleWaiters = [];

const trackRequest = async (promise) => {
  pendingRequests++;
  try {
    return await promise;
  } finally {
    pendingRequests--;
    if (pendingRequests === 0) {
      const waiters = idleWaiters;
      idleWaiters = [];
      waiters.forEach((resolve) => resolve(true));
    }
  }
};

export const getPendingRequestCount = () => pendingRequests;

export const waitForRequestsIdle = (timeoutMs = 60000) =>
  new Promise((resolve) => {
    if (pendingRequests === 0) return resolve(true);
    const done = (value) => {
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => {
      idleWaiters = idleWaiters.filter((w) => w !== done);
      resolve(false);
    }, timeoutMs);
    idleWaiters.push(done);
  });

export const fetchData = async (
  endpoint,
  method = "GET",
  data = null,
  headers = {},
  params = {},
  navigate,
  navigatePath = null,
  dispatch,
  sortKey = null
) => {
  const localToken = localStorage.getItem("token");
  const token = validateToken(localToken);

  if (!token) {
    console.warn("Invalid or missing token.");
    if (dispatch) {
      dispatch(logout());
    }
    if (navigate) {
      navigate("/login");
    }
    return;
  }

  const pageKey =
    String(method).toUpperCase() === "GET" && params && params.page !== undefined
      ? `${endpoint}|${params.limit ?? params.per_page ?? ""}`
      : null;
  const requestNumber = pageKey ? (latestPageRequest[pageKey] = (latestPageRequest[pageKey] || 0) + 1) : 0;
  const isStale = () => pageKey !== null && latestPageRequest[pageKey] !== requestNumber;

  try {
    const response = await trackRequest(
      axios({
        url: `${process.env.REACT_APP_BASE_URL}/api/${endpoint}`,
        method,
        data,
        headers: {
          Authorization: `Bearer ${token}`,
          ...headers,
        },
        params,
      })
    );

    if (isStale()) return new Promise(() => {});

    const responseData = response?.data;

    if (response.status === 200 || response.status === 201) {
      if (responseData?.status_code === 200 || responseData?.status_code === 201) {
        toast.success(responseData.message || "Success");

        if (navigate && navigatePath) {
          navigate(navigatePath);
        }
      }

      if (sortKey && responseData?.[sortKey]) {
        responseData[sortKey].sort((a, b) => a.name.localeCompare(b.name));
      }

      return responseData;
    } else {
      console.warn("Unexpected response format:", response);
      return responseData;
    }
  } catch (error) {
    //console.error("Error fetching data:", error);

    // 🔹 Handle Unauthorized (401) or Inactive (403)
    handleAuthError(error, dispatch, navigate);

    if (isStale()) return new Promise(() => {});
    throw error.response?.data || "An error occurred while fetching data.";
  }
};

  export const postData = async (endpoint, data, headers = {}, params = {}, navigate, navigatePath = null, dispatch) => {
    return fetchData(endpoint, "POST", data, headers, params, navigate, navigatePath, dispatch);
};

export const putData = async (endpoint, data, headers = {}, params = {}, navigate, navigatePath = null, dispatch) => {
  return fetchData(endpoint, "PUT", data, headers, params, navigate, navigatePath, dispatch);
};

/**
 * Normalize Backend API Response to Frontend Structure
 * 
 * Backend Schema (PostgreSQL):
 * - updated_by: 0 (Pending), 1 (DQA Approved), -1 (DQA Rejected), 2 (PM Approved), -2 (PM Rejected)
 * - remarks: rejection reason
 * - updated_at: timestamp of action
 * 
 * Frontend Structure:
 * - is_dqa_approved: 1 (approved), 0 (rejected), null (pending)
 * - dqa_approved_at, dqa_rejected_at, dqa_rejection_reason
 * - is_pm_approved: 1 (approved), 0 (rejected), null (pending)
 * - pm_approved_at, pm_rejected_at, pm_rejection_reason
 */

/**
 * Normalize a single feature from backend format to frontend format
 * @param {Object} backendFeature - Raw feature data from PostgreSQL backend
 * @returns {Object} Normalized feature object matching frontend structure
 */
export const normalizeFeatureData = (backendFeature) => {
  // Handle case where backendFeature is null/undefined
  if (!backendFeature) {
    return null;
  }

  // Extract backend fields
  const {
    id,
    village_id,
    village_name,
    feature_name,
    percentage,
    created_at,
    updated_by,
    updated_at,
    remarks,
  } = backendFeature;

  // Initialize frontend fields
  let is_dqa_approved = null;
  let dqa_approved_at = null;
  let dqa_rejected_at = null;
  let dqa_rejection_reason = null;

  let is_pm_approved = null;
  let pm_approved_at = null;
  let pm_rejected_at = null;
  let pm_rejection_reason = null;

  // Map updated_by to DQA approval fields
  if (updated_by === 1) {
    // DQA Approved
    is_dqa_approved = 1;
    dqa_approved_at = updated_at;
  } else if (updated_by === -1) {
    // DQA Rejected
    is_dqa_approved = 0;
    dqa_rejected_at = updated_at;
    dqa_rejection_reason = remarks;
  }

  // Map updated_by to PM approval fields
  if (updated_by === 2) {
    // PM Approved
    is_pm_approved = 1;
    pm_approved_at = updated_at;
    // If PM approved, DQA must have approved first
    is_dqa_approved = 1;
  } else if (updated_by === -2) {
    // PM Rejected
    is_pm_approved = 0;
    pm_rejected_at = updated_at;
    pm_rejection_reason = remarks;
    // If PM rejected, DQA must have approved first
    is_dqa_approved = 1;
  }

  // Return normalized object matching frontend structure
  return {
    id,
    village_id,
    village: village_name,
    feature: feature_name,
    percentage: percentage ? `${percentage}%` : "0%",
    createdAt: formatDate(created_at),
    is_dqa_approved,
    dqa_approved_at: formatDateTime(dqa_approved_at),
    dqa_rejected_at: formatDateTime(dqa_rejected_at),
    dqa_rejection_reason,
    is_pm_approved,
    pm_approved_at: formatDateTime(pm_approved_at),
    pm_rejected_at: formatDateTime(pm_rejected_at),
    pm_rejection_reason,
  };
};

/**
 * Format date to YYYY-MM-DD format
 * @param {string|Date} dateString - Date to format
 * @returns {string|null} Formatted date string or null
 */
const formatDate = (dateString) => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return null;
  }
};

/**
 * Format datetime to "MMM DD, YYYY HH:MM AM/PM" format
 * @param {string|Date} dateTimeString - DateTime to format
 * @returns {string|null} Formatted datetime string or null
 */
export const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return null;

  try {
    const date = new Date(dateTimeString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.error("Error formatting datetime:", error);
    return null;
  }
};

export const formatDateDMY = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "N/A";
  }
};

export const formatDateTimeDMY = (dateTimeString) => {
  if (!dateTimeString) return null;

  try {
    const date = new Date(dateTimeString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const time = date.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${day}-${month}-${year}, ${time}`;
  } catch (error) {
    console.error("Error formatting datetime:", error);
    return null;
  }
};

/**
 * Normalize array of features
 * @param {Array} backendFeatures - Array of backend feature objects
 * @returns {Array} Array of normalized feature objects
 */
export const normalizeFeaturesList = (backendFeatures) => {
  if (!Array.isArray(backendFeatures)) {
    console.warn("Expected array of features, received:", backendFeatures);
    return [];
  }

  return backendFeatures
    .map(normalizeFeatureData)
    .filter((feature) => feature !== null);
};

/**
 * Filter features based on user permissions
 * @param {Array} features - Normalized features array
 * @param {Object} permissions - User permission object
 * @returns {Array} Filtered features array
 */
export const filterFeaturesByPermission = (features, permissions) => {
  const {
    hasDQAApprovePermission = false,
    hasPMApprovePermission = false,
    isSuperAdmin = false,
  } = permissions;

  // Super Admin or both permissions: see all
  if (isSuperAdmin || (hasDQAApprovePermission && hasPMApprovePermission)) {
    return features;
  }

  // DQA only: see all features
  if (hasDQAApprovePermission && !hasPMApprovePermission) {
    return features;
  }

  // PM only: see only DQA approved features
  if (hasPMApprovePermission && !hasDQAApprovePermission) {
    return features.filter((feature) => feature.is_dqa_approved === 1);
  }

  // No permissions: see nothing
  return [];
};

/**
 * Prepare approval/rejection payload for backend
 * @param {string} action - 'approve' or 'reject'
 * @param {string} role - 'dqa' or 'pm'
 * @param {string} reason - Rejection reason (required for reject action)
 * @returns {Object} Payload object for backend API
 */
export const prepareApprovalPayload = (action, role, reason = null) => {
  let updated_by;

  // Determine updated_by value based on role and action
  if (role === "dqa") {
    updated_by = action === "approve" ? 1 : -1;
  } else if (role === "pm") {
    updated_by = action === "approve" ? 2 : -2;
  } else {
    throw new Error(`Invalid role: ${role}. Must be 'dqa' or 'pm'`);
  }

  const payload = {
    updated_by,
    updated_at: new Date().toISOString(),
  };

  // Add remarks only for rejection
  if (action === "reject") {
    if (!reason || !reason.trim()) {
      throw new Error("Rejection reason is required");
    }
    payload.remarks = reason.trim();
  }

  return payload;
};

/**
 * Example backend response structure for reference
 */
export const BACKEND_SCHEMA_EXAMPLE = {
  id: 101,
  village_id: 1,
  village_name: "Village A",
  feature_name: "Water Pump Installation",
  percentage: 80,
  created_at: "2025-09-20T00:00:00.000Z",
  updated_by: 1, // 0: Pending, 1: DQA Approved, -1: DQA Rejected, 2: PM Approved, -2: PM Rejected
  updated_at: "2025-09-21T10:30:00.000Z",
  remarks: "Incomplete documentation", // Only for rejections
  approval_status: null, // Redundant field, ignore
};

/**
 * Example frontend structure for reference
 */
export const FRONTEND_SCHEMA_EXAMPLE = {
  id: 101,
  village_id: 1,
  village: "Village A",
  feature: "Water Pump Installation",
  percentage: "80%",
  createdAt: "2025-09-20",
  is_dqa_approved: 1, // 1: approved, 0: rejected, null: pending
  dqa_approved_at: "Sep 21, 2025 10:30 AM",
  dqa_rejected_at: null,
  dqa_rejection_reason: null,
  is_pm_approved: null,
  pm_approved_at: null,
  pm_rejected_at: null,
  pm_rejection_reason: null,
};

export default {
  normalizeFeatureData,
  normalizeFeaturesList,
  filterFeaturesByPermission,
  prepareApprovalPayload,
  formatDateDMY,
  formatDateTimeDMY,
};

export const submitFormWithErrors = (formik) => async (e) => {
    formik.handleSubmit(e);
    const errors = await formik.validateForm();
    const fields = Object.keys(errors);
    if (fields.length === 0) return;

    const firstField = fields
      .map((name) =>
        document.querySelector(`[name="${name}"], [id="${name}"]`)
      )
      .find(Boolean);
    if (firstField) {
      firstField.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
